import React, {useCallback, useEffect, useState} from 'react';
import {ActivityIndicator, Pressable, StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import {GeocodedSuburb, Shop} from '../models/middleware';
import SuburbSelectionModal from '../screens/storeSelection/SuburbSelectionModal';
import {useStores} from '../stores/use-stores';

import Text from './ui-controls/Text';
import {Icon} from './Icon';
import StoreItem from './StoreItem';

interface StaffStorePickerProps {
  mode?: 'compact' | 'full';
  onStoreSelected: (shop: Shop) => void;
  selectedStore?: Shop | null;
  placeholder?: string;
  testID?: string;
  // DIS-1308: skip the trigger Pressable and open the suburb modal on
  // mount so the parent screen can drive store changes from its own link
  // without rendering a redundant placeholder + Cancel scaffold.
  hideTrigger?: boolean;
  autoOpenModal?: boolean;
  onModalCancel?: () => void;
}

const StoreSeparator = () => <View style={styles.separator} />;

const StaffStorePicker = observer(
  ({
    mode = 'compact',
    onStoreSelected,
    selectedStore,
    placeholder = 'Select your store',
    testID = 'staff-store-picker',
    hideTrigger = false,
    autoOpenModal = false,
    onModalCancel,
  }: StaffStorePickerProps) => {
    const {shopSelectionStore} = useStores();
    const [modalVisible, setModalVisible] = useState(autoOpenModal);
    const [suburbSelected, setSuburbSelected] = useState(false);
    const [loadStartedAt, setLoadStartedAt] = useState<number | null>(null);
    const [graceTick, setGraceTick] = useState(0);

    const handleOpenModal = () => setModalVisible(true);
    const handleCloseModal = () => {
      setModalVisible(false);
      // DIS-1308: notify parent when user dismisses the modal before
      // a suburb was picked, so the change-store affordance can collapse.
      if (!suburbSelected) {
        onModalCancel?.();
      }
    };

    const handleSelectSuburb = (suburb: GeocodedSuburb) => {
      shopSelectionStore.resetLoading();
      shopSelectionStore.selectSuburb(suburb);
      setSuburbSelected(true);
      setLoadStartedAt(Date.now());
      setModalVisible(false);
    };

    useEffect(() => {
      if (loadStartedAt === null) return;
      const elapsed = Date.now() - loadStartedAt;
      const remaining = 300 - elapsed;
      if (remaining <= 0) return;
      const t = setTimeout(() => setGraceTick(n => n + 1), remaining);
      return () => clearTimeout(t);
    }, [loadStartedAt]);

    const handleSelectStore = useCallback(
      (shop: Shop) => {
        onStoreSelected(shop);
        setSuburbSelected(false);
      },
      [onStoreSelected],
    );

    const dayName = new Date().toLocaleString('en-AU', {weekday: 'long'}).toLowerCase();

    const renderTrigger = () => (
      <Pressable
        style={styles.trigger}
        onPress={handleOpenModal}
        testID="staff-store-picker-trigger">
        {selectedStore ? (
          <View style={styles.selectedStoreInfo}>
            <Text style={styles.storeName} numberOfLines={1}>
              {selectedStore.name}
            </Text>
            <Text style={styles.storeAddress} numberOfLines={1}>
              {selectedStore.address?.locality}, {selectedStore.address?.administrative_area}{' '}
              {selectedStore.address?.postal_code}
            </Text>
          </View>
        ) : (
          <Text style={styles.placeholderText}>{placeholder}</Text>
        )}
        <Icon name="chevron-right" size={16} color="#999" />
      </Pressable>
    );

    const renderFullModeHeader = () => (
      <Pressable
        style={styles.searchSuburbButton}
        onPress={handleOpenModal}
        testID="search-suburb-button">
        <Icon name="magnifying-glass" size={16} color="#666" />
        <Text style={styles.searchSuburbButtonText}>
          {shopSelectionStore.selectedSuburb
            ? `${shopSelectionStore.selectedSuburb.locality} — Change suburb`
            : 'Search suburb or postcode'}
        </Text>
      </Pressable>
    );

    const renderStoreList = () => {
      if (!suburbSelected) return null;

      const withinGrace = loadStartedAt !== null && Date.now() - loadStartedAt < 300;
      // graceTick referenced to force re-render at grace expiry
      void graceTick;

      // Show loading if MobX says loading, OR we're inside the grace window
      // AND the list is still empty (prevents empty-state flash). If the list
      // is already populated, render it immediately even within grace.
      const hasStores = shopSelectionStore.storeList.length > 0;
      const isLoading = shopSelectionStore.isStoreListLoading() || (withinGrace && !hasStores);

      if (isLoading) {
        return (
          <View style={styles.loadingContainer} testID="staff-store-picker-loading">
            <ActivityIndicator size="large" />
          </View>
        );
      }

      if (!hasStores) {
        return (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No stores found</Text>
            <Pressable
              style={styles.retryButton}
              onPress={handleOpenModal}
              testID="search-another-suburb">
              <Text style={styles.retryButtonText}>Search another suburb</Text>
            </Pressable>
          </View>
        );
      }

      return (
        <View style={styles.storeList}>
          {shopSelectionStore.storeList.map((item, idx) => (
            <React.Fragment key={item.store_number}>
              {idx > 0 ? <StoreSeparator /> : null}
              <StoreItem item={item} dayName={dayName} selectStore={handleSelectStore} />
            </React.Fragment>
          ))}
        </View>
      );
    };

    return (
      <View style={styles.container} testID={testID}>
        {hideTrigger ? null : mode === 'compact' ? renderTrigger() : renderFullModeHeader()}

        <SuburbSelectionModal
          isVisible={modalVisible}
          onSelectSuburb={handleSelectSuburb}
          onCancel={handleCloseModal}
          title="SELECT YOUR STORE"
        />

        {renderStoreList()}
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {},
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  selectedStoreInfo: {
    flex: 1,
    flexDirection: 'column',
  },
  storeName: {
    fontSize: 16,
    fontWeight: '600',
  },
  storeAddress: {
    fontSize: 14,
    color: '#666',
    marginTop: 2,
  },
  placeholderText: {
    fontSize: 16,
    color: '#999',
  },
  searchSuburbButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    backgroundColor: '#f9f9f9',
    marginBottom: 8,
  },
  searchSuburbButtonText: {
    fontSize: 16,
    color: '#666',
    marginLeft: 8,
  },
  loadingContainer: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  emptyContainer: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
  },
  retryButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#007AFF',
  },
  separator: {
    marginVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: 'lightgrey',
  },
  storeList: {
    marginTop: 8,
  },
});

export default StaffStorePicker;
